import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type AuthMeResponse } from "@/lib/api/client";
import { useAuthSession, useAuthUser } from "@/components/providers/auth-context";

export interface UseProvisioningResult {
  isProvisioned: boolean;
  isProvisioning: boolean;
  provisioningError: Error | null;
  profile: AuthMeResponse | null;
  refetch: () => void;
}

/**
 * Hook to ensure user is provisioned in the backend
 *
 * Calls /auth/me after authentication to:
 * 1. Provision tenant/user if this is a new sign-up
 * 2. Retrieve user profile and tenant info
 *
 * Passes email/name from Descope SDK since these may not be in the JWT.
 *
 * Other data-fetching hooks should depend on this being complete
 * before making requests.
 */
export function useProvisioning(): UseProvisioningResult {
  const { isAuthenticated, isSessionLoading } = useAuthSession();
  const { user, isUserLoading } = useAuthUser();
  const queryClient = useQueryClient();

  const enabled = isAuthenticated && !isSessionLoading && !isUserLoading;

  const {
    data,
    isPending,
    isFetching,
    error,
    refetch,
    isSuccess,
  } = useQuery({
    queryKey: ["auth", "me", user?.email],
    queryFn: () =>
      api.auth.me({
        email: user?.email,
        name: user?.name,
      }),
    // Wait for both session and user data to be available
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 (not authenticated) or 400 (bad request)
      if (error instanceof Error && "status" in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 400) {
          return false;
        }
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
  });

  // isChecking is true when:
  // 1. User data is still loading (isUserLoading)
  // 2. Query is actively fetching (isFetching)
  // 3. Query is enabled but hasn't completed yet (enabled && isPending)
  // This distinguishes "checking status" from "not yet ready to check"
  const isChecking = isUserLoading || isFetching || (enabled && isPending);

  return {
    isProvisioned: isSuccess && !!data,
    isProvisioning: isChecking,
    provisioningError: error as Error | null,
    profile: data ?? null,
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      refetch();
    },
  };
}
